/**
 * Context Event Form Component
 * =============================
 * 
 * Form for creating and editing context events.
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X, 
  Calendar, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Save,
  AlertCircle,
  Repeat,
  RefreshCw,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

interface ContextEvent {
  id: string;
  event_type: string;
  event_name: string;
  description: string | null;
  start_date: string;
  end_date: string | null;
  is_recurring: boolean;
  recurrence_rule: string | null;
  expected_impact_direction: 'up' | 'down' | 'mixed' | null;
  expected_impact_pct_low: number | null;
  expected_impact_pct_high: number | null;
  affected_channels: string[];
  affected_platforms: string[];
  source: string;
  confidence: number;
  tags: string[];
  notes: string | null;
}

interface FormData {
  event_type: string;
  event_name: string;
  description: string;
  start_date: string;
  end_date: string;
  is_recurring: boolean;
  recurrence_rule: string;
  expected_impact_direction: string;
  expected_impact_pct_low: string;
  expected_impact_pct_high: string;
  affected_channels: string[];
  affected_platforms: string[];
  notes: string;
}

interface Props {
  event?: ContextEvent | null;
  onClose: () => void;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

async function createEvent(data: Partial<FormData>): Promise<ContextEvent> {
  const res = await fetch('/api/admin/context-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || 'Failed to create event');
  }
  return res.json();
}

async function updateEvent(id: string, data: Partial<FormData>): Promise<ContextEvent> {
  const res = await fetch(`/api/admin/context-events/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || 'Failed to update event');
  }
  return res.json();
}

// =============================================================================
// CONSTANTS
// =============================================================================

const EVENT_TYPES = [
  { value: 'internal_promo', label: 'Internal Promo', description: 'Your own promotions' },
  { value: 'retailer_promo', label: 'Retailer Promo', description: 'Retailer-specific events (Ulta 21 Days, etc.)' },
  { value: 'competitor_activity', label: 'Competitor Activity', description: 'Competitor promos or launches' },
  { value: 'industry_event', label: 'Industry Event', description: 'Industry-wide events (Prime Day, Black Friday)' },
  { value: 'seasonality', label: 'Seasonality', description: 'Seasonal patterns (post-holiday, Valentine\'s)' },
  { value: 'custom', label: 'Custom', description: 'Other events' },
];

const CHANNELS = [
  { value: 'all', label: '🌐 All Channels', desc: 'Affects both retail & DTC' },
  { value: 'retail', label: '🏪 In-Store Retail', desc: 'Ulta, Macy\'s, JCP, Sephora' },
  { value: 'dtc', label: '🛒 DTC / Online', desc: 'Shopify, Amazon, Walmart.com' },
];

const RETAIL_PLATFORMS = [
  { value: 'ulta', label: '🏪 Ulta' },
  { value: 'macys', label: "🏪 Macy's" },
  { value: 'jcp', label: '🏪 JCPenney' },
  { value: 'sephora', label: '🏪 Sephora' },
  { value: 'target', label: '🏪 Target' },
  { value: 'nordstrom', label: '🏪 Nordstrom' },
];

const DTC_PLATFORMS = [
  { value: 'shopify', label: '🛒 Shopify' },
  { value: 'amazon', label: '🛒 Amazon' },
  { value: 'walmart.com', label: '🛒 Walmart.com' },
];

const PLATFORMS = [...RETAIL_PLATFORMS, ...DTC_PLATFORMS];

const RECURRENCE_RULES = [
  { value: '', label: 'No recurrence (one-time)' },
  { value: 'yearly', label: 'Every year (same dates)' },
  { value: 'yearly_black_friday', label: 'Black Friday (4th Thurs Nov + weekend)' },
  { value: 'yearly_cyber_monday', label: 'Cyber Monday (Mon after Thanksgiving)' },
  { value: 'yearly_prime_day', label: 'Prime Day (July, specific dates TBD)' },
  { value: 'yearly_week_47', label: 'Same week every year (Week 47)' },
  { value: 'yearly_march_september', label: 'Twice yearly (March & September)' },
  { value: 'quarterly', label: 'Quarterly (every 3 months)' },
  { value: 'monthly', label: 'Monthly (same day each month)' },
];

// =============================================================================
// COMPONENT
// =============================================================================

const ContextEventForm: React.FC<Props> = ({ event, onClose }) => {
  const queryClient = useQueryClient();
  const isEditing = !!event;
  
  const [formData, setFormData] = useState<FormData>({
    event_type: event?.event_type || 'custom',
    event_name: event?.event_name || '',
    description: event?.description || '',
    start_date: event?.start_date || new Date().toISOString().split('T')[0],
    end_date: event?.end_date || '',
    is_recurring: event?.is_recurring || false,
    recurrence_rule: event?.recurrence_rule || '',
    expected_impact_direction: event?.expected_impact_direction || '',
    expected_impact_pct_low: event?.expected_impact_pct_low?.toString() || '',
    expected_impact_pct_high: event?.expected_impact_pct_high?.toString() || '',
    affected_channels: event?.affected_channels || ['all'],
    affected_platforms: event?.affected_platforms || [],
    notes: event?.notes || '',
  });
  
  const [error, setError] = useState<string | null>(null);
  
  const createMutation = useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-events'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });
  
  const updateMutation = useMutation({
    mutationFn: (data: Partial<FormData>) => updateEvent(event!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-events'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!formData.event_name.trim()) {
      setError('Event name is required');
      return;
    }
    if (!formData.start_date) {
      setError('Start date is required');
      return;
    }
    
    // Prepare data
    const submitData = {
      ...formData,
      expected_impact_pct_low: formData.expected_impact_pct_low ? parseFloat(formData.expected_impact_pct_low) : undefined,
      expected_impact_pct_high: formData.expected_impact_pct_high ? parseFloat(formData.expected_impact_pct_high) : undefined,
      expected_impact_direction: formData.expected_impact_direction || undefined,
      end_date: formData.end_date || undefined,
      recurrence_rule: formData.recurrence_rule || undefined,
    };
    
    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };
  
  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleChannelToggle = (channel: string) => {
    if (channel === 'all') {
      handleChange('affected_channels', ['all']);
    } else {
      const current = formData.affected_channels.filter(c => c !== 'all');
      if (current.includes(channel)) {
        handleChange('affected_channels', current.filter(c => c !== channel));
      } else {
        handleChange('affected_channels', [...current, channel]);
      }
    }
  };
  
  const handlePlatformToggle = (platform: string) => {
    if (formData.affected_platforms.includes(platform)) {
      handleChange('affected_platforms', formData.affected_platforms.filter(p => p !== platform));
    } else {
      handleChange('affected_platforms', [...formData.affected_platforms, platform]);
    }
  };
  
  const isLoading = createMutation.isPending || updateMutation.isPending;
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {isEditing ? 'Edit Event' : 'Add Context Event'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Event Type</label>
            <div className="grid grid-cols-2 gap-2">
              {EVENT_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleChange('event_type', type.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    formData.event_type === type.value
                      ? 'border-amber-500 bg-amber-500/10 text-white'
                      : 'border-gray-700 hover:border-gray-600 text-gray-400'
                  }`}
                >
                  <div className="font-medium text-sm">{type.label}</div>
                  <div className="text-xs opacity-70">{type.description}</div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Event Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Event Name *</label>
            <input
              type="text"
              value={formData.event_name}
              onChange={(e) => handleChange('event_name', e.target.value)}
              placeholder="e.g., Ulta 21 Days of Beauty"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the event and its expected impact..."
              rows={3}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none"
            />
          </div>
          
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
              />
            </div>
          </div>
          
          {/* Recurring Event */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleChange('is_recurring', !formData.is_recurring)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  formData.is_recurring ? 'bg-amber-500' : 'bg-gray-700'
                }`}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  formData.is_recurring ? 'translate-x-6' : ''
                }`} />
              </button>
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-300">Recurring Event</span>
              </div>
            </div>
            
            {formData.is_recurring && (
              <div className="pl-[60px]">
                <label className="block text-sm font-medium text-gray-300 mb-2">Recurrence Pattern</label>
                <select
                  value={formData.recurrence_rule}
                  onChange={(e) => handleChange('recurrence_rule', e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                >
                  {RECURRENCE_RULES.map(rule => (
                    <option key={rule.value} value={rule.value}>
                      {rule.label}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />
                  The system will automatically create future instances based on this pattern.
                </p>
              </div>
            )}
          </div>
          
          {/* Expected Impact */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Expected Impact Direction</label>
            <div className="flex gap-2">
              {[
                { value: 'up', label: 'Up', icon: <TrendingUp className="w-4 h-4" />, color: 'emerald' },
                { value: 'down', label: 'Down', icon: <TrendingDown className="w-4 h-4" />, color: 'rose' },
                { value: 'mixed', label: 'Mixed', icon: <Minus className="w-4 h-4" />, color: 'amber' },
              ].map(dir => (
                <button
                  key={dir.value}
                  type="button"
                  onClick={() => handleChange('expected_impact_direction', dir.value)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border transition-colors ${
                    formData.expected_impact_direction === dir.value
                      ? `border-${dir.color}-500 bg-${dir.color}-500/10 text-${dir.color}-400`
                      : 'border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  {dir.icon}
                  {dir.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Impact Range */}
          {formData.expected_impact_direction && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Impact % (Low)
                </label>
                <input
                  type="number"
                  value={formData.expected_impact_pct_low}
                  onChange={(e) => handleChange('expected_impact_pct_low', e.target.value)}
                  placeholder={formData.expected_impact_direction === 'down' ? '-40' : '10'}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Impact % (High)
                </label>
                <input
                  type="number"
                  value={formData.expected_impact_pct_high}
                  onChange={(e) => handleChange('expected_impact_pct_high', e.target.value)}
                  placeholder={formData.expected_impact_direction === 'down' ? '-20' : '30'}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                />
              </div>
            </div>
          )}
          
          {/* Affected Channels */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Affected Channels
              <span className="ml-2 text-xs text-gray-500">Which channels does this event impact?</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map(channel => (
                <button
                  key={channel.value}
                  type="button"
                  onClick={() => handleChannelToggle(channel.value)}
                  className={`flex flex-col items-start px-4 py-2 rounded-lg border transition-colors ${
                    formData.affected_channels.includes(channel.value)
                      ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                      : 'border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <span className="font-medium">{channel.label}</span>
                  <span className="text-xs opacity-70">{channel.desc}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              💡 Select "All" for events like Prime Day. Select specific channels for events like "Ulta 21 Days" (Retail only).
            </p>
          </div>
          
          {/* Affected Platforms */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Specific Platforms <span className="text-gray-500">(optional - leave empty for all platforms in selected channels)</span>
            </label>
            
            {/* Retail Platforms */}
            {(formData.affected_channels.includes('all') || formData.affected_channels.includes('retail')) && (
              <div className="mb-3">
                <span className="text-xs text-gray-500 mb-1 block">Retail Platforms:</span>
                <div className="flex flex-wrap gap-2">
                  {RETAIL_PLATFORMS.map(platform => (
                    <button
                      key={platform.value}
                      type="button"
                      onClick={() => handlePlatformToggle(platform.value)}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        formData.affected_platforms.includes(platform.value)
                          ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                          : 'border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      {platform.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* DTC Platforms */}
            {(formData.affected_channels.includes('all') || formData.affected_channels.includes('dtc')) && (
              <div>
                <span className="text-xs text-gray-500 mb-1 block">DTC Platforms:</span>
                <div className="flex flex-wrap gap-2">
                  {DTC_PLATFORMS.map(platform => (
                    <button
                      key={platform.value}
                      type="button"
                      onClick={() => handlePlatformToggle(platform.value)}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        formData.affected_platforms.includes(platform.value)
                          ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                          : 'border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      {platform.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <p className="mt-2 text-xs text-gray-500">
              💡 Example: "Ulta 21 Days of Beauty" → Select "Retail" channel + "Ulta" platform only
            </p>
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any additional notes..."
              rows={2}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none"
            />
          </div>
          
          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-black font-medium rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              {isLoading ? 'Saving...' : isEditing ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContextEventForm;
